import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import NotificationBell from './NotificationBell';
import DecisionGraphPanel from './DecisionGraphPanel';
import AuditLogPanel from './AuditLogPanel';
import PlanPanel from './PlanPanel';
import AISettingsPanel from './AISettingsPanel';
import AdminDashboardPanel from './AdminDashboardPanel';
import { Wordmark } from './BrandMark';
import TodayView, { TodoRow } from './TodayView';
import { CommandBar, AskSheet } from './AskAimy';
import CollaboratorsBadge from './CollaboratorsBadge';
import Toast from './Toast';
import InstallPrompt from './InstallPrompt';
import VerifyEmailBanner from './VerifyEmailBanner';
import BackButton from './BackButton';
import ItemDetailModal from './ItemDetailModal';
import CreateWorkspacePanel from './CreateWorkspacePanel';
import InviteTeammates from './InviteTeammates';
import usePwaInstall from '../hooks/usePwaInstall';
import { updateWorkspaceArtifact } from '../utils/artifacts';
import { copyText, inviteLink, sendBulkInvites } from '../utils/invites';
import { track } from '../analytics';
import { EntityIcon, EditableIcon, isImageIcon } from './EntityIcon';
import {
  IconDecision,
  IconGraph,
  IconArtifact,
  IconTodo,
  IconMessage,
  IconClose,
  IconSparkle,
  IconEdit,
  IconSettings,
  IconPeople,
  IconPlus,
  IconBoard,
  IconChart,
  IconWorkspace,
} from './Icons';
import './Lobby.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';
const ROLE_RANK = { owner: 4, admin: 3, member: 2, viewer: 1 };
const isWorkspaceAdmin = (role) => (ROLE_RANK[role] || 0) >= ROLE_RANK.admin;
// Delegated member permissions (server: `requireWorkspaceManage`): admins and
// owners always manage; plain members do too while the workspace has the
// delegation switched on (the default). Covers the people-management actions
// only — inviting, revoking invites, removing members. Renaming the workspace
// and changing the AI teammates stay admin-only (`isAdmin`). Mirrors the server
// gate so the UI shows exactly the actions the API will accept — the server is
// still the authority.
const canManageWorkspace = (role, workspace) =>
  isWorkspaceAdmin(role) || (role === 'member' && workspace?.memberPermissionsEnabled !== false);
// Viewers are read-only; member+ may edit workspace content (e.g. an artefact
// opened in the lightbox). Unlike the people-management gate above this is not
// delegable — it mirrors the server's requireWorkspaceRole('member').
const canEditContent = (role) => (ROLE_RANK[role] || ROLE_RANK.member) >= ROLE_RANK.member;

// Mirrors the server's snippetOf (workspace-insights.js) so a locally-edited
// artefact row shows the same one-line preview the rollup would have sent.
function snippetOf(text, max = 160) {
  if (!text) return '';
  const flat = String(text).replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  return `${flat.slice(0, max - 1).trimEnd()}…`;
}

// Maps the 0–100 workspace health score to a colour bucket. Drives the
// `.health-good` / `.health-warn` / `.health-bad` CSS classes on the badge.
function healthBucket(score) {
  if (score >= 75) return 'good';
  if (score >= 50) return 'warn';
  return 'bad';
}

const ROOM_TEMPLATES = [
  { id: 'freeform', label: 'Freeform', description: 'Open-ended conversation.' },
  { id: 'brainstorm', label: 'Brainstorm', description: 'Generate and explore ideas.' },
  { id: 'code-review', label: 'Code Review', description: 'Review code, discuss design.' },
  { id: 'planning', label: 'Planning', description: 'Break down work, set goals.' },
  {
    id: 'retrospective',
    label: 'Retrospective',
    description: 'Reflect on what went well and what to improve.',
  },
];

// Mirrors the avatar set offered at registration (UserSetup.js) so editing a
// profile presents the same choices as signing up.
const PROFILE_AVATARS = ['👤', '🧑‍💻', '👩‍💼', '🧑‍🔬', '👨‍🎨', '🧑‍🏫', '👩‍🚀', '🦊', '🐔', '🐧'];

// The Lobby's tabbed views. "Today" is the Direction B home stream (default);
// "Threads" is the original room grid; Decisions/Artefacts are workspace-wide
// rollups served by GET /api/workspaces/:id/landing; "Todos" is the flat
// workspace todo list served by GET /api/workspaces/:id/todos.
const VIEWS = [
  { id: 'today', label: 'Today' },
  { id: 'threads', label: 'Threads' },
  { id: 'decisions', label: 'Decisions' },
  { id: 'todos', label: 'Todos' },
  { id: 'artifacts', label: 'Artefacts' },
];

function Lobby({
  user,
  rooms,
  connected,
  workspaces,
  currentWorkspaceId,
  activeThreadId = null,
  onSwitchWorkspace,
  onCreateWorkspace,
  onDeleteWorkspace,
  onLeaveWorkspace,
  onUpdateWorkspaceIcon,
  onRenameWorkspace,
  // Merges a partial workspace update (e.g. the member-permissions toggle)
  // back into App state so every permission gate re-evaluates at once.
  onWorkspaceUpdated,
  onCreateThread,
  onJoinThread,
  onDeleteThread,
  onLogout,
  onUpdateProfile,
  lastSeenCounts,
  onlineUsers = {},
  notifications = [],
  unreadNotificationCount = 0,
  onMarkNotificationRead,
  onMarkAllNotificationsRead,
  // Cross-workspace notifications for the global bell next to the username.
  allNotifications = [],
  allUnreadCount = 0,
  onMarkGlobalNotificationRead,
  onMarkAllGlobalNotificationsRead,
  onNavigateGlobalNotification,
  // Current workspace's full member roster — powers the bottom-left
  // "Collaborators" badge on the Today home screen.
  workspaceMembers = [],
  // Told whether the create-workspace panel is open, so App can hold the
  // first-run tour back until the new workspace (and its invite link) is done.
  onWorkspaceSetupChange,
}) {
  const [newThreadName, setNewThreadName] = useState('');
  const [newThreadTemplate, setNewThreadTemplate] = useState('freeform');
  const [showInvitePanel, setShowInvitePanel] = useState(false);
  const [showNewWorkspace, setShowNewWorkspace] = useState(false);
  const [health, setHealth] = useState(null);
  const [catchup, setCatchup] = useState(null);
  const [showCatchup, setShowCatchup] = useState(false);
  const [loadingCatchup, setLoadingCatchup] = useState(false);
  const [showCreateThread, setShowCreateThread] = useState(false);
  const [showDecisionGraph, setShowDecisionGraph] = useState(false);
  const [showPlanPanel, setShowPlanPanel] = useState(false);
  const [showAISettings, setShowAISettings] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showWorkspaceMenu, setShowWorkspaceMenu] = useState(false);
  const [showWorkspaceSwitcher, setShowWorkspaceSwitcher] = useState(false);
  const [statsExpanded, setStatsExpanded] = useState(false);
  // Tabbed landing views (Today / Threads / Decisions / Todos / Artefacts).
  const [activeView, setActiveView] = useState('today');
  const [landing, setLanding] = useState(null);
  const [todos, setTodos] = useState(null);
  // The decision / artefact / todo opened "to the fore" in the detail reader.
  const [detailItem, setDetailItem] = useState(null);
  // Today "Mark caught up" — the persisted per-(user,workspace) timestamp and a
  // transient toast shared with the rest of the Lobby's confirmations.
  const [caughtUpAt, setCaughtUpAt] = useState(null);
  const [lobbyToast, setLobbyToast] = useState(null);
  const lobbyToastTimer = useRef(null);
  // Today AI briefing (server generates; degrades to a facts line) + the
  // Ask-Aimy sheet toggle.
  const [briefing, setBriefing] = useState(null);
  const [askOpen, setAskOpen] = useState(false);
  const [loadingLanding, setLoadingLanding] = useState(false);
  const [viewFilter, setViewFilter] = useState('');
  const [viewSort, setViewSort] = useState('recent');
  const userMenuRef = useRef(null);
  const workspaceMenuRef = useRef(null);
  const workspaceSwitcherRef = useRef(null);

  // PWA install: capture the browser's install prompt and surface it both as a
  // one-time first-login nudge and as an "Install as an app" user-menu item.
  const pwa = usePwaInstall();
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  // "Confirm your email" strip. Dismissed for the session only — the account
  // still works unverified, so re-nagging on the next visit is the right
  // pressure and needs no persistence.
  const [verifyBannerDismissed, setVerifyBannerDismissed] = useState(false);
  useEffect(() => {
    if (!pwa.installable || !user?.id) return;
    // Once per browser per user — this is the "first login" nudge.
    const key = `symphonate-install-prompted-${user.id}`;
    let alreadyPrompted = false;
    try {
      alreadyPrompted = window.localStorage.getItem(key) === '1';
    } catch {
      alreadyPrompted = false;
    }
    if (alreadyPrompted) return;
    try {
      window.localStorage.setItem(key, '1');
    } catch {
      // ignore storage errors — worst case we nudge again next load
    }
    setShowInstallPrompt(true);
  }, [pwa.installable, user?.id]);

  const handleInstallApp = useCallback(() => {
    setShowUserMenu(false);
    // Native dialog when available (Chrome/Edge/Android); otherwise show the
    // banner, which carries the iOS Share → Add to Home Screen instructions.
    if (pwa.canPrompt) {
      pwa.promptInstall();
    } else {
      setShowInstallPrompt(true);
    }
  }, [pwa]);

  // Inline workspace-rename state (admin+ only).
  const [renamingWorkspace, setRenamingWorkspace] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');

  // Edit-profile modal state.
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editName, setEditName] = useState(user.name || '');
  const [editAvatar, setEditAvatar] = useState(user.avatar || PROFILE_AVATARS[0]);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  // Email-digest opt-ins live in user_preferences (weekly is the GTM retention
  // hook). App.js only tracks AI-routing prefs, so the Edit-profile modal loads
  // and saves these itself against the same PATCH /api/me/preferences endpoint.
  const [digestWeekly, setDigestWeekly] = useState(false);
  const [digestDaily, setDigestDaily] = useState(false);
  // Change-password sub-form (authenticated in-app password reset).
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordNotice, setPasswordNotice] = useState('');

  const resetPasswordForm = useCallback(() => {
    setShowChangePassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordError('');
    setPasswordNotice('');
  }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordNotice('');
    if (newPassword !== confirmNewPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('symphonate-token');
      const res = await fetch(`${SERVER_URL}/api/me/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPasswordError(data.error || 'Could not change password');
      } else {
        setPasswordNotice(data.message || 'Your password has been changed.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setShowChangePassword(false);
      }
    } catch {
      setPasswordError('Could not connect to server');
    }
    setChangingPassword(false);
  };

  const openEditProfile = useCallback(() => {
    setEditName(user.name || '');
    setEditAvatar(user.avatar || PROFILE_AVATARS[0]);
    setProfileError('');
    resetPasswordForm();
    setShowEditProfile(true);
    const token = localStorage.getItem('symphonate-token');
    if (!token) return;
    fetch(`${SERVER_URL}/api/me/preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.preferences) {
          setDigestWeekly(!!data.preferences.digestWeekly);
          setDigestDaily(!!data.preferences.digestDaily);
        }
      })
      .catch(() => {
        /* leave the toggles at their last-known state */
      });
  }, [user.name, user.avatar, resetPasswordForm]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    const trimmed = editName.trim();
    if (!trimmed || savingProfile) return;
    setSavingProfile(true);
    setProfileError('');
    try {
      await onUpdateProfile({ name: trimmed, avatar: editAvatar });
      // Persist the digest opt-ins (separate endpoint from the profile PATCH).
      // Best-effort: a failure here shouldn't undo a successful profile save.
      const token = localStorage.getItem('symphonate-token');
      if (token) {
        await fetch(`${SERVER_URL}/api/me/preferences`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ digestWeekly, digestDaily }),
        }).catch(() => {});
      }
      setShowEditProfile(false);
    } catch (err) {
      setProfileError(err.message || 'Could not update profile');
    }
    setSavingProfile(false);
  };

  // Close the user menu when clicking outside or pressing Escape.
  useEffect(() => {
    if (!showUserMenu) return;
    const onClick = (e) => {
      if (!userMenuRef.current?.contains(e.target)) setShowUserMenu(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setShowUserMenu(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showUserMenu]);

  // Close the workspace settings menu on outside-click / Escape.
  useEffect(() => {
    if (!showWorkspaceMenu) return;
    const onClick = (e) => {
      if (!workspaceMenuRef.current?.contains(e.target)) setShowWorkspaceMenu(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setShowWorkspaceMenu(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showWorkspaceMenu]);

  // Close the workspace switcher (name chevron) on outside-click / Escape.
  useEffect(() => {
    if (!showWorkspaceSwitcher) return;
    const onClick = (e) => {
      if (!workspaceSwitcherRef.current?.contains(e.target)) setShowWorkspaceSwitcher(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setShowWorkspaceSwitcher(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showWorkspaceSwitcher]);

  const hasMultipleWorkspaces = Array.isArray(workspaces) && workspaces.length > 1;
  // An account that belongs to no workspace (a fresh signup, or one that left
  // them all) gets the "create your workspace" step instead of the Today
  // stream. Only an explicit empty list counts, so callers that don't pass
  // `workspaces` keep the ordinary lobby.
  const needsWorkspace = Array.isArray(workspaces) && workspaces.length === 0;

  useEffect(() => {
    onWorkspaceSetupChange?.(showNewWorkspace);
  }, [showNewWorkspace, onWorkspaceSetupChange]);
  const currentWorkspace = (workspaces || []).find((w) => w.id === currentWorkspaceId) || null;
  const currentRole = currentWorkspace?.role || null;
  const isAdmin = isWorkspaceAdmin(currentRole);
  // People management (invite, revoke, remove members) is open to members too
  // while the workspace delegates it. Everything else — rename, AI teammates,
  // audit log, the member-permissions toggle, blocking, thread deletion —
  // stays on `isAdmin`.
  const canManage = canManageWorkspace(currentRole, currentWorkspace);
  // Any member can open the AI teammates panel; it renders read-only unless
  // they're an admin (the server's GET is member+, the PATCH is admin+).
  const canViewAISettings = (ROLE_RANK[currentRole] || 0) >= ROLE_RANK.member;
  // "Leave workspace" is for non-admin members only — admins/owners manage the
  // workspace (and an owner leaving could orphan it), so they delete instead.
  const canLeaveWorkspace = !!currentWorkspace && !isAdmin && !!onLeaveWorkspace;
  // Whether the "This workspace" section has any item to show for this role.
  const hasWorkspaceSection = isAdmin || canManage || canViewAISettings || canLeaveWorkspace;

  const startRenameWorkspace = () => {
    if (!currentWorkspace) return;
    setRenameDraft(currentWorkspace.name || '');
    setRenamingWorkspace(true);
  };

  const cancelRenameWorkspace = () => {
    setRenamingWorkspace(false);
    setRenameDraft('');
  };

  const commitRenameWorkspace = async () => {
    const trimmed = renameDraft.trim();
    if (!currentWorkspace || !onRenameWorkspace || !trimmed || trimmed === currentWorkspace.name) {
      cancelRenameWorkspace();
      return;
    }
    await onRenameWorkspace(currentWorkspace.id, trimmed);
    cancelRenameWorkspace();
  };

  const handleDeleteWorkspace = async (workspace) => {
    if (!workspace || !onDeleteWorkspace) return;
    const confirmed = window.confirm(
      `Delete workspace "${workspace.name}"?\n\n` +
        'Members will lose access. Threads will be preserved as legacy threads ' +
        'and re-adopted on the next server boot. This cannot be undone.',
    );
    if (!confirmed) return;
    const result = await onDeleteWorkspace(workspace.id);
    if (result?.error) window.alert(result.error);
  };

  const handleLeaveWorkspace = async (workspace) => {
    if (!workspace || !onLeaveWorkspace) return;
    const confirmed = window.confirm(
      `Leave workspace "${workspace.name}"?\n\n` +
        'You will lose access to its threads until you are invited back.',
    );
    if (!confirmed) return;
    setShowWorkspaceMenu(false);
    const result = await onLeaveWorkspace(workspace.id);
    if (result?.error) window.alert(result.error);
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!newThreadName.trim()) return;
    onCreateThread(newThreadName.trim(), newThreadTemplate);
    setNewThreadName('');
    setNewThreadTemplate('freeform');
    setShowCreateThread(false);
  };

  // Idea #10: keep the workspace health score fresh whenever we load the
  // lobby for a different workspace. Pure GET, ~one SQL query worth of work.
  useEffect(() => {
    if (!currentWorkspaceId) {
      setHealth(null);
      return;
    }
    const token = localStorage.getItem('symphonate-token');
    if (!token) return;
    let cancelled = false;
    fetch(`${SERVER_URL}/api/workspaces/${currentWorkspaceId}/health`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled) setHealth(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentWorkspaceId]);

  // Workspace-wide rollups for the Decisions / Artefacts / Plans views. Cheap
  // SQL (no AI tokens), fetched alongside health so the tab counts are correct
  // on first paint and the views are instant when the user switches to them.
  useEffect(() => {
    if (!currentWorkspaceId) {
      setLanding(null);
      return;
    }
    const token = localStorage.getItem('symphonate-token');
    if (!token) return;
    let cancelled = false;
    setLoadingLanding(true);
    fetch(`${SERVER_URL}/api/workspaces/${currentWorkspaceId}/landing`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled) setLanding(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingLanding(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentWorkspaceId]);

  // Workspace-wide todos backing the Today "Needs you" card + Todos view.
  // Cheap SQL; refetched on workspace switch. `refreshTodos` lets an optimistic
  // toggle (Phase 3) reconcile with the server.
  const refreshTodos = useCallback(() => {
    if (!currentWorkspaceId) {
      setTodos(null);
      return;
    }
    const token = localStorage.getItem('symphonate-token');
    if (!token) return;
    fetch(`${SERVER_URL}/api/workspaces/${currentWorkspaceId}/todos`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setTodos(data?.todos || null))
      .catch(() => {});
  }, [currentWorkspaceId]);

  useEffect(() => {
    refreshTodos();
  }, [refreshTodos]);

  // Today "caught up" timestamp (per user, per workspace). Cheap GET on switch.
  useEffect(() => {
    if (!currentWorkspaceId) {
      setCaughtUpAt(null);
      return;
    }
    const token = localStorage.getItem('symphonate-token');
    if (!token) return;
    let cancelled = false;
    fetch(`${SERVER_URL}/api/workspaces/${currentWorkspaceId}/caught-up`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled) setCaughtUpAt(data?.caughtUpAt || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentWorkspaceId]);

  // Shared transient toast (capture confirmations, "caught up").
  const showToast = useCallback((text) => {
    setLobbyToast(text);
    if (lobbyToastTimer.current) clearTimeout(lobbyToastTimer.current);
    lobbyToastTimer.current = setTimeout(() => setLobbyToast(null), 1800);
  }, []);
  useEffect(() => () => lobbyToastTimer.current && clearTimeout(lobbyToastTimer.current), []);

  // Save an artefact edited in the lightbox (the Artefacts view opens rows "to
  // the fore" without joining their thread, so this goes over REST). The row is
  // patched in place afterwards rather than refetching the whole rollup; anyone
  // in the artefact's thread gets the server's `artifacts-updated` broadcast.
  const handleSaveArtifact = useCallback(
    async (item, { title, content }) => {
      const updated = await updateWorkspaceArtifact(currentWorkspaceId, item.id, {
        title,
        content,
      });
      const at = updated?.updatedAt || new Date().toISOString();
      setLanding((prev) =>
        prev
          ? {
              ...prev,
              artifacts: (prev.artifacts || []).map((a) =>
                a.id === item.id
                  ? { ...a, title, body: content, snippet: snippetOf(content), at }
                  : a,
              ),
            }
          : prev,
      );
      setDetailItem((prev) =>
        prev && prev.id === item.id ? { ...prev, title, body: content, at } : prev,
      );
      showToast('Artefact updated');
    },
    [currentWorkspaceId, showToast],
  );

  // "The workspace is caught up" — a stamp exists and no thread has newer
  // activity than it. Collapses the Today briefing to its ambient line.
  const caughtUp = useMemo(() => {
    if (!caughtUpAt) return false;
    const cutoff = Date.parse(caughtUpAt);
    if (Number.isNaN(cutoff)) return false;
    return !rooms.some((r) => r.lastMessage && Date.parse(r.lastMessage.at) > cutoff);
  }, [caughtUpAt, rooms]);

  const handleMarkCaughtUp = useCallback(() => {
    if (!currentWorkspaceId) return;
    const token = localStorage.getItem('symphonate-token');
    // Optimistic: stamp now; reconcile with the server's timestamp on success.
    setCaughtUpAt(new Date().toISOString());
    showToast('You’re all caught up');
    track('caught-up', { workspaceId: currentWorkspaceId });
    fetch(`${SERVER_URL}/api/workspaces/${currentWorkspaceId}/caught-up`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data?.caughtUpAt && setCaughtUpAt(data.caughtUpAt))
      .catch(() => {});
  }, [currentWorkspaceId, showToast]);

  // Toggle a workspace todo from the Today "Needs you" card / Todos view.
  // Optimistic flip, then PATCH; refetch to reconcile (and pick up the live
  // socket broadcast for anyone in the thread).
  const handleToggleTodo = useCallback(
    (todo) => {
      if (!currentWorkspaceId || !todo?.id) return;
      const token = localStorage.getItem('symphonate-token');
      setTodos((prev) =>
        (prev || []).map((t) => (t.id === todo.id ? { ...t, completed: !t.completed } : t)),
      );
      fetch(`${SERVER_URL}/api/workspaces/${currentWorkspaceId}/todos/${todo.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => {
          if (!r.ok) throw new Error('toggle failed');
        })
        .catch(() => {})
        .finally(refreshTodos);
    },
    [currentWorkspaceId, refreshTodos],
  );

  // Today AI briefing (GET, cached server-side). Degrades to a facts line, so
  // it's always safe to render whatever comes back.
  useEffect(() => {
    if (!currentWorkspaceId) {
      setBriefing(null);
      return;
    }
    const token = localStorage.getItem('symphonate-token');
    if (!token) return;
    let cancelled = false;
    fetch(`${SERVER_URL}/api/workspaces/${currentWorkspaceId}/briefing`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled) setBriefing(data?.text || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentWorkspaceId]);

  // Ask Aimy about the workspace — resolves to the answer text for the sheet.
  const handleAsk = useCallback(
    async (question) => {
      if (!currentWorkspaceId) return '';
      const token = localStorage.getItem('symphonate-token');
      const res = await fetch(`${SERVER_URL}/api/workspaces/${currentWorkspaceId}/ask`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ question }),
      });
      if (res.status === 429) return 'One moment — try that again in a second.';
      if (!res.ok) throw new Error('ask failed');
      const data = await res.json();
      track('ask-submitted', { workspaceId: currentWorkspaceId });
      return data.text || '';
    },
    [currentWorkspaceId],
  );

  // Idea #7: workspace catch-up. Fetched lazily on click — most users won't
  // open it on every load, so we don't pay for it up front.
  const handleOpenCatchup = useCallback(async () => {
    if (!currentWorkspaceId) return;
    setShowCatchup(true);
    setLoadingCatchup(true);
    try {
      const token = localStorage.getItem('symphonate-token');
      const res = await fetch(`${SERVER_URL}/api/workspaces/${currentWorkspaceId}/catchup`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCatchup(await res.json());
      else setCatchup(null);
    } catch {
      setCatchup(null);
    } finally {
      setLoadingCatchup(false);
    }
  }, [currentWorkspaceId]);

  // Sort: active rooms (with participants) first, then by creation date desc
  const sortedThreads = [...rooms].sort((a, b) => {
    if (a.participantCount > 0 && b.participantCount === 0) return -1;
    if (a.participantCount === 0 && b.participantCount > 0) return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const activeCount = rooms.filter((r) => r.participantCount > 0).length;
  const openDecisions = rooms.reduce((s, r) => s + (r.decisionCount || 0), 0);
  const activeTodos = rooms.reduce((s, r) => s + (r.todoCount || 0), 0);
  const totalUnread = rooms.reduce((s, r) => {
    const seen = (lastSeenCounts || {})[r.id] || 0;
    // chatMessageCount excludes "joined / left" system messages so unread
    // doesn't tick up just because someone backgrounded the app.
    const visible = r.chatMessageCount ?? r.messageCount ?? 0;
    return s + Math.max(0, visible - seen);
  }, 0);

  // Per-tab counts. Threads is always known locally; the rest come from the
  // landing fetch (undefined until it lands, so the badge simply hides).
  const decisionsTotal = health?.decisionsTotal ?? landing?.decisions?.length ?? 0;
  const landingItems =
    activeView === 'decisions'
      ? landing?.decisions
      : activeView === 'artifacts'
        ? landing?.artifacts
        : null;

  return (
    <div className="lobby">
      <div className="lobby-container">
        <header className="lobby-header">
          <div className="lobby-header-top">
            <h1>
              <Wordmark size="1.4rem" withMark />
            </h1>
            <div className="lobby-header-account">
              {onMarkGlobalNotificationRead && (
                <NotificationBell
                  global
                  notifications={allNotifications}
                  unreadCount={allUnreadCount}
                  onMarkRead={onMarkGlobalNotificationRead}
                  onMarkAllRead={onMarkAllGlobalNotificationsRead}
                  onNavigate={onNavigateGlobalNotification}
                />
              )}
              <div className="user-menu-wrapper" ref={userMenuRef}>
                <button
                  type="button"
                  className="user-pill"
                  onClick={() => setShowUserMenu((v) => !v)}
                  aria-haspopup="true"
                  aria-expanded={showUserMenu}
                  title={connected ? 'Account · online' : 'Account · offline'}
                >
                  <span className={`status-dot ${connected ? 'online' : 'offline'}`} />
                  {isImageIcon(user.avatar) ? (
                    <EntityIcon icon={user.avatar} name={user.name} size={20} />
                  ) : (
                    <span className="user-pill-avatar">{user.avatar}</span>
                  )}
                  <span className="user-pill-name">{user.name}</span>
                  <span className="user-pill-chevron" aria-hidden="true">
                    ▾
                  </span>
                </button>
                {showUserMenu && (
                  <div className="user-menu" role="menu">
                    <div className="user-menu-header">
                      <div className="user-menu-name">
                        {isImageIcon(user.avatar) ? (
                          <EntityIcon icon={user.avatar} name={user.name} size={20} />
                        ) : (
                          <span>{user.avatar}</span>
                        )}
                        <strong>{user.name}</strong>
                      </div>
                      {currentRole && (
                        <span
                          className={`role-badge role-${currentRole}`}
                          title={`Your role: ${currentRole}`}
                        >
                          {currentRole}
                        </span>
                      )}
                    </div>

                    {onUpdateProfile && (
                      <button
                        type="button"
                        className="user-menu-item user-menu-action"
                        onClick={() => {
                          setShowUserMenu(false);
                          openEditProfile();
                        }}
                      >
                        Edit profile
                      </button>
                    )}

                    {pwa.installable && (
                      <button
                        type="button"
                        className="user-menu-item user-menu-action"
                        onClick={handleInstallApp}
                      >
                        <IconPlus size={14} /> Install as an app
                      </button>
                    )}

                    {currentWorkspace && (
                      <button
                        type="button"
                        className="user-menu-item user-menu-action"
                        onClick={() => {
                          setShowUserMenu(false);
                          setShowPlanPanel(true);
                        }}
                      >
                        <IconDecision size={14} /> Plan &amp; upgrade
                      </button>
                    )}

                    {user.isPlatformAdmin && (
                      <>
                        <div className="user-menu-divider" />
                        <button
                          type="button"
                          className="user-menu-item user-menu-action"
                          onClick={() => {
                            setShowUserMenu(false);
                            setShowAdminDashboard(true);
                          }}
                        >
                          Admin dashboard
                        </button>
                      </>
                    )}

                    {onLogout && (
                      <>
                        <div className="user-menu-divider" />
                        <button
                          type="button"
                          className="user-menu-item user-menu-action"
                          onClick={() => {
                            setShowUserMenu(false);
                            onLogout();
                          }}
                        >
                          Log out
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          {currentWorkspace && (
            <div className="workspace-summary">
              <div className="workspace-summary-head">
                <EditableIcon
                  icon={currentWorkspace.icon}
                  name={currentWorkspace.name}
                  size={28}
                  canEdit={isAdmin && !!onUpdateWorkspaceIcon}
                  onChange={(value) => onUpdateWorkspaceIcon(currentWorkspace.id, value)}
                  title="Change workspace icon"
                />
                {renamingWorkspace ? (
                  <input
                    type="text"
                    className="workspace-rename-input"
                    value={renameDraft}
                    autoFocus
                    maxLength={120}
                    aria-label="Workspace name"
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onBlur={commitRenameWorkspace}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitRenameWorkspace();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelRenameWorkspace();
                      }
                    }}
                  />
                ) : (
                  <div className="workspace-switcher-wrapper" ref={workspaceSwitcherRef}>
                    <button
                      type="button"
                      className={`workspace-summary-toggle ${showWorkspaceSwitcher ? 'expanded' : ''}`}
                      onClick={() => setShowWorkspaceSwitcher((v) => !v)}
                      aria-haspopup="true"
                      aria-expanded={showWorkspaceSwitcher}
                      title="Switch workspace"
                    >
                      <span className="workspace-summary-chevron" aria-hidden="true">
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                          <polygon points="2,1 8,5 2,9" />
                        </svg>
                      </span>
                      <span className="workspace-summary-name">
                        {currentWorkspace?.name || 'Workspace'}
                      </span>
                    </button>
                    {showWorkspaceSwitcher && (
                      <div className="user-menu workspace-menu workspace-switcher-menu" role="menu">
                        <div className="user-menu-section">
                          {(workspaces || []).map((w) => {
                            const isCurrent = w.id === currentWorkspaceId;
                            const canDelete =
                              onDeleteWorkspace && w.role === 'owner' && hasMultipleWorkspaces;
                            return (
                              <div
                                key={w.id}
                                className={`user-menu-workspace-row ${isCurrent ? 'current' : ''}`}
                              >
                                <button
                                  type="button"
                                  className={`user-menu-item user-menu-workspace ${isCurrent ? 'current' : ''}`}
                                  onClick={() => {
                                    if (!isCurrent && onSwitchWorkspace) onSwitchWorkspace(w.id);
                                    setShowWorkspaceSwitcher(false);
                                  }}
                                >
                                  <span
                                    className={`user-menu-check ${isCurrent ? 'active' : ''}`}
                                    aria-hidden={!isCurrent}
                                  >
                                    {isCurrent ? '✓' : ''}
                                  </span>
                                  <EntityIcon icon={w.icon} name={w.name} size={18} />
                                  <span className="user-menu-workspace-name">{w.name}</span>
                                </button>
                                {canDelete && (
                                  <button
                                    type="button"
                                    className="user-menu-workspace-delete"
                                    title={`Delete workspace "${w.name}"`}
                                    aria-label={`Delete workspace ${w.name}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowWorkspaceSwitcher(false);
                                      handleDeleteWorkspace(w);
                                    }}
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            );
                          })}
                          {onCreateWorkspace && (
                            <button
                              type="button"
                              className="user-menu-item user-menu-action"
                              onClick={() => {
                                setShowNewWorkspace(true);
                                setShowWorkspaceSwitcher(false);
                              }}
                            >
                              <IconPlus size={14} /> New workspace
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="workspace-settings-wrapper" ref={workspaceMenuRef}>
                  <button
                    type="button"
                    className="workspace-settings-btn"
                    onClick={() => setShowWorkspaceMenu((v) => !v)}
                    title="Workspace settings"
                    aria-label="Workspace settings"
                    aria-haspopup="true"
                    aria-expanded={showWorkspaceMenu}
                  >
                    <IconSettings size={16} />
                  </button>
                  {showWorkspaceMenu && (
                    <div className="user-menu workspace-menu" role="menu">
                      <button
                        type="button"
                        className="user-menu-item user-menu-action"
                        role="menuitemcheckbox"
                        aria-checked={statsExpanded}
                        onClick={() => setStatsExpanded((v) => !v)}
                      >
                        <span className="user-menu-item-label">
                          <IconChart size={14} /> Show statistics
                        </span>
                        <span
                          className={`user-menu-check ${statsExpanded ? 'active' : ''}`}
                          aria-hidden={!statsExpanded}
                        >
                          {statsExpanded ? '✓' : ''}
                        </span>
                      </button>

                      {/* "This workspace" actions follow the workspace's
                          delegation setting: rename / invite / AI teammates are
                          open to members while it's on, audit log stays
                          admin-only, and viewers get only "Leave workspace".
                          Hide the divider + section header entirely when nothing
                          renders for this role (otherwise an empty
                          "This workspace" header shows). */}
                      {hasWorkspaceSection && (
                        <>
                          <div className="user-menu-divider" />
                          <div className="user-menu-eyebrow">This workspace</div>
                        </>
                      )}
                      {isAdmin && !!onRenameWorkspace && (
                        <button
                          type="button"
                          className="user-menu-item user-menu-action"
                          onClick={() => {
                            setShowWorkspaceMenu(false);
                            startRenameWorkspace();
                          }}
                        >
                          <IconEdit size={14} /> Rename workspace
                        </button>
                      )}
                      {canManage && (
                        <button
                          type="button"
                          className="user-menu-item user-menu-action"
                          onClick={() => {
                            setShowWorkspaceMenu(false);
                            setShowInvitePanel(true);
                          }}
                        >
                          <IconPeople size={14} /> Members &amp; invites
                        </button>
                      )}
                      {canViewAISettings && (
                        <button
                          type="button"
                          className="user-menu-item user-menu-action"
                          onClick={() => {
                            setShowWorkspaceMenu(false);
                            setShowAISettings(true);
                          }}
                        >
                          <IconSparkle size={14} /> AI teammates
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          type="button"
                          className="user-menu-item user-menu-action"
                          onClick={() => {
                            setShowWorkspaceMenu(false);
                            setShowAuditLog(true);
                          }}
                        >
                          <IconBoard size={14} /> Audit log
                        </button>
                      )}
                      {canLeaveWorkspace && (
                        <button
                          type="button"
                          className="user-menu-item user-menu-action user-menu-danger"
                          onClick={() => handleLeaveWorkspace(currentWorkspace)}
                        >
                          <IconClose size={14} /> Leave workspace
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {statsExpanded && (
                <div
                  id="workspace-summary-stats"
                  className="workspace-summary-bar"
                  aria-label="Workspace summary"
                >
                  <div className="summary-cell">
                    <span className="summary-icon" aria-hidden="true">
                      <IconDecision size={14} />
                    </span>
                    <span className="summary-num">{openDecisions}</span>
                    <span className="summary-label">
                      open {openDecisions === 1 ? 'decision' : 'decisions'}
                    </span>
                  </div>
                  <div className="summary-cell">
                    <span className="summary-icon" aria-hidden="true">
                      <IconTodo size={14} />
                    </span>
                    <span className="summary-num">{activeTodos}</span>
                    <span className="summary-label">
                      active {activeTodos === 1 ? 'todo' : 'todos'}
                    </span>
                  </div>
                  <div className="summary-cell">
                    <span className="summary-icon" aria-hidden="true">
                      <IconArtifact size={14} />
                    </span>
                    <span className="summary-num">{activeCount}</span>
                    <span className="summary-label">
                      {activeCount === 1 ? 'thread active' : 'threads active'}
                    </span>
                  </div>
                  <div className="summary-cell">
                    <span className="summary-icon" aria-hidden="true">
                      <IconMessage size={14} />
                    </span>
                    <span className={`summary-num ${totalUnread > 0 ? 'has-unread' : ''}`}>
                      {totalUnread}
                    </span>
                    <span className="summary-label">unread</span>
                  </div>
                  {health && typeof health.decisionsTotal === 'number' && (
                    <div
                      className="summary-cell"
                      title="Every decision is captured and queryable — ask your AI teammate “why did we decide X?” anytime."
                    >
                      <span className="summary-num">{health.decisionsTotal}</span>
                      <span className="summary-label">
                        {health.decisionsTotal === 1
                          ? 'decision remembered'
                          : 'decisions remembered'}
                      </span>
                    </div>
                  )}
                  {health && typeof health.pendingInviteCount === 'number' && (
                    <div
                      className="summary-cell"
                      title="People invited whose invite is still pending (not yet accepted)."
                    >
                      <span className="summary-icon" aria-hidden="true">
                        <IconPeople size={14} />
                      </span>
                      <span className="summary-num">{health.pendingInviteCount}</span>
                      <span className="summary-label">invited</span>
                    </div>
                  )}
                  {health && typeof health.memberCount === 'number' && (
                    <div className="summary-cell" title="People who have joined this workspace.">
                      <span className="summary-icon" aria-hidden="true">
                        <IconPeople size={14} />
                      </span>
                      <span className="summary-num">{health.memberCount}</span>
                      <span className="summary-label">signed up</span>
                    </div>
                  )}
                  {health && typeof health.posterCount === 'number' && (
                    <div
                      className="summary-cell"
                      title="Members who have posted at least one message in this workspace."
                    >
                      <span className="summary-icon" aria-hidden="true">
                        <IconMessage size={14} />
                      </span>
                      <span className="summary-num">{health.posterCount}</span>
                      <span className="summary-label">posted</span>
                    </div>
                  )}
                  {health && (
                    <div
                      className={`summary-cell health-cell health-${healthBucket(health.score)}`}
                      title={(health.signals || []).map((s) => s.text).join('\n')}
                    >
                      <span className="summary-num">{health.score}</span>
                      <span className="summary-label">workspace health</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </header>

        {!verifyBannerDismissed && (
          <VerifyEmailBanner user={user} onDismiss={() => setVerifyBannerDismissed(true)} />
        )}

        {showNewWorkspace && (
          <CreateWorkspacePanel
            user={user}
            firstWorkspace={needsWorkspace}
            onCreateWorkspace={onCreateWorkspace}
            onClose={() => setShowNewWorkspace(false)}
          />
        )}

        {/* First run: an account with no workspace yet (new signups no longer
            get a pre-made "personal" one) is asked to create one — which is
            also where they invite their team. */}
        {needsWorkspace && !showNewWorkspace && (
          <div className="workspace-welcome">
            <span className="workspace-welcome-icon" aria-hidden="true">
              <IconWorkspace size={22} />
            </span>
            <h2>Create your workspace</h2>
            <p>Create your workspace to collaborate with your team.</p>
            {onCreateWorkspace && (
              <button
                type="button"
                className="btn-primary"
                onClick={() => setShowNewWorkspace(true)}
              >
                Create workspace
              </button>
            )}
          </div>
        )}

        {showEditProfile && (
          <div
            className="edit-profile-backdrop"
            onClick={() => {
              if (!savingProfile) setShowEditProfile(false);
            }}
          >
            <div
              className="edit-profile-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Edit profile"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="edit-profile-header">
                <h2>Edit profile</h2>
                <button
                  type="button"
                  className="btn-close-panel"
                  onClick={() => setShowEditProfile(false)}
                  aria-label="Close"
                >
                  x
                </button>
              </div>
              <form onSubmit={handleSaveProfile} className="edit-profile-form">
                <label className="edit-profile-label" htmlFor="edit-profile-name">
                  Display name
                </label>
                <input
                  id="edit-profile-name"
                  type="text"
                  value={editName}
                  onChange={(e) => {
                    setEditName(e.target.value);
                    setProfileError('');
                  }}
                  placeholder="Your name..."
                  maxLength={30}
                  autoFocus
                />
                <span className="edit-profile-label">Avatar</span>
                <div className="edit-avatar-row">
                  <EditableIcon
                    icon={editAvatar}
                    name={editName || user.name}
                    size={48}
                    canEdit
                    onChange={(value) => setEditAvatar(value)}
                    title="Upload an image or pick an emoji"
                  />
                  <div className="edit-avatar-grid">
                    {PROFILE_AVATARS.map((a) => (
                      <button
                        key={a}
                        type="button"
                        className={`edit-avatar-btn ${editAvatar === a ? 'selected' : ''}`}
                        onClick={() => setEditAvatar(a)}
                        aria-pressed={editAvatar === a}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
                <span className="edit-profile-hint">
                  Click the large avatar to upload an image (max 2MB) — it&apos;s resized
                  automatically.
                </span>
                <span className="edit-profile-label">Email notifications</span>
                <label className="edit-profile-check">
                  <input
                    type="checkbox"
                    checked={digestWeekly}
                    onChange={(e) => setDigestWeekly(e.target.checked)}
                  />
                  <span>
                    Weekly decisions digest
                    <em> — a Friday recap of the decisions your team made this week</em>
                  </span>
                </label>
                <label className="edit-profile-check">
                  <input
                    type="checkbox"
                    checked={digestDaily}
                    onChange={(e) => setDigestDaily(e.target.checked)}
                  />
                  <span>
                    Daily standup digest
                    <em> — a lightweight morning summary</em>
                  </span>
                </label>
                <span className="edit-profile-label">Password</span>
                {!showChangePassword ? (
                  <div className="change-password-row">
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => {
                        setShowChangePassword(true);
                        setPasswordError('');
                        setPasswordNotice('');
                      }}
                    >
                      Change password
                    </button>
                    {passwordNotice && <span className="notice-text">{passwordNotice}</span>}
                  </div>
                ) : (
                  <div className="change-password-fields">
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => {
                        setCurrentPassword(e.target.value);
                        setPasswordError('');
                      }}
                      placeholder="Current password"
                      autoComplete="current-password"
                    />
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setPasswordError('');
                      }}
                      placeholder="New password (6+ chars)"
                      autoComplete="new-password"
                    />
                    <input
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => {
                        setConfirmNewPassword(e.target.value);
                        setPasswordError('');
                      }}
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                    />
                    {passwordError && <span className="error-text">{passwordError}</span>}
                    <div className="change-password-actions">
                      <button
                        type="button"
                        className="btn-close-panel"
                        onClick={resetPasswordForm}
                        disabled={changingPassword}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={handleChangePassword}
                        disabled={
                          changingPassword ||
                          !currentPassword ||
                          !newPassword ||
                          !confirmNewPassword
                        }
                      >
                        {changingPassword ? 'Updating...' : 'Update password'}
                      </button>
                    </div>
                  </div>
                )}
                {profileError && <span className="error-text">{profileError}</span>}
                <div className="edit-profile-actions">
                  <button
                    type="button"
                    className="btn-close-panel"
                    onClick={() => setShowEditProfile(false)}
                    disabled={savingProfile}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={!editName.trim() || savingProfile}
                  >
                    {savingProfile ? 'Saving...' : 'Save changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showInvitePanel && currentWorkspace && canManage && (
          <InvitePanel
            workspace={currentWorkspace}
            user={user}
            isAdmin={isAdmin}
            onWorkspaceUpdated={onWorkspaceUpdated}
            onClose={() => setShowInvitePanel(false)}
          />
        )}

        {showAuditLog && currentWorkspace && isAdmin && (
          <AuditLogPanel
            workspaceId={currentWorkspaceId}
            workspaceName={currentWorkspace?.name || 'this workspace'}
            onClose={() => setShowAuditLog(false)}
          />
        )}

        {showAdminDashboard && user.isPlatformAdmin && (
          <AdminDashboardPanel
            currentUserId={user.id}
            onClose={() => setShowAdminDashboard(false)}
          />
        )}

        {showCatchup && (
          <CatchupModal
            workspaceName={currentWorkspace?.name || 'this workspace'}
            data={catchup}
            loading={loadingCatchup}
            onClose={() => setShowCatchup(false)}
            onJumpToThread={(roomId) => {
              setShowCatchup(false);
              onJoinThread(roomId);
            }}
          />
        )}

        {showPlanPanel && currentWorkspace && (
          <PlanPanel
            workspaceId={currentWorkspace.id}
            workspaceName={currentWorkspace.name}
            onClose={() => setShowPlanPanel(false)}
          />
        )}
        {showAISettings && currentWorkspace && canViewAISettings && (
          <AISettingsPanel
            workspaceId={currentWorkspace.id}
            workspaceName={currentWorkspace.name}
            readOnly={!isAdmin}
            onClose={() => setShowAISettings(false)}
          />
        )}
        {showDecisionGraph && currentWorkspace && (
          <DecisionGraphPanel
            workspaceId={currentWorkspaceId}
            workspaceName={currentWorkspace?.name || 'this workspace'}
            onClose={() => setShowDecisionGraph(false)}
            onJumpToThread={(roomId) => {
              setShowDecisionGraph(false);
              onJoinThread(roomId);
            }}
          />
        )}

        {showCreateThread && (
          <div
            className="new-thread-backdrop"
            onClick={() => setShowCreateThread(false)}
            role="presentation"
          >
            <div
              className="new-thread-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label="New thread"
            >
              <div className="new-thread-header">
                <h2>New thread</h2>
                <button
                  className="btn-close-panel"
                  onClick={() => setShowCreateThread(false)}
                  aria-label="Close"
                >
                  x
                </button>
              </div>
              <form onSubmit={handleCreate} className="new-thread-form">
                <input
                  type="text"
                  value={newThreadName}
                  onChange={(e) => setNewThreadName(e.target.value)}
                  placeholder="Thread name (e.g. Sprint Planning, Design Review)…"
                  maxLength={50}
                  autoFocus
                />
                <div className="template-picker" role="radiogroup" aria-label="Template">
                  {ROOM_TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`template-chip ${newThreadTemplate === t.id ? 'selected' : ''}`}
                      onClick={() => setNewThreadTemplate(t.id)}
                      title={t.description}
                      role="radio"
                      aria-checked={newThreadTemplate === t.id}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <button type="submit" className="btn-primary" disabled={!newThreadName.trim()}>
                  Create thread
                </button>
              </form>
            </div>
          </div>
        )}

        <div className="rooms-list">
          {/* The header row (back button + workspace action icons) is only for
              subviews and the all-threads grid — the Today landing page keeps a
              clean top, with notifications moved up next to the username. */}
          {!needsWorkspace && activeView !== 'today' && (
            <div className="threads-header-row is-subview">
              <div className="stream-subview-lead">
                <BackButton
                  title="Back to Today"
                  onClick={() => {
                    setActiveView('today');
                    setViewFilter('');
                  }}
                />
                <h2 className="stream-subview-title">
                  {(VIEWS.find((v) => v.id === activeView) || {}).label || 'Today'}
                </h2>
              </div>
              <div className="threads-header-actions">
                {onMarkNotificationRead && (
                  <NotificationBell
                    notifications={notifications}
                    unreadCount={unreadNotificationCount}
                    onMarkRead={onMarkNotificationRead}
                    onMarkAllRead={onMarkAllNotificationsRead}
                    onNavigate={onJoinThread}
                  />
                )}
                {currentWorkspace && (
                  <button
                    className="btn-catchup-icon"
                    onClick={() => setShowDecisionGraph(true)}
                    title="Decision Graph — see how decisions connect"
                    aria-label="Open the decision graph"
                  >
                    <IconGraph size={14} />
                  </button>
                )}
                {currentWorkspace && (
                  <button
                    className="btn-catchup-icon"
                    onClick={handleOpenCatchup}
                    title="Catch up — see what's been happening"
                    aria-label="Catch up on this workspace"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <polygon points="3,4 14,12 3,20" />
                      <polygon points="13,4 24,12 13,20" />
                    </svg>
                  </button>
                )}
                <button
                  className="btn-new-thread btn-new-thread-icon"
                  onClick={() => setShowCreateThread(true)}
                  title="Start a new thread"
                  aria-label="Start a new thread"
                >
                  +
                </button>
              </div>
            </div>
          )}
          {needsWorkspace ? null : activeView === 'today' ? (
            <TodayView
              user={user}
              rooms={rooms}
              todos={todos || []}
              decisionsTotal={decisionsTotal}
              artefactsTotal={landing?.artifacts?.length ?? 0}
              lastSeenCounts={lastSeenCounts}
              onlineUsers={onlineUsers}
              caughtUp={caughtUp}
              briefing={briefing}
              activeThreadId={activeThreadId}
              onMarkCaughtUp={handleMarkCaughtUp}
              onToggleTodo={handleToggleTodo}
              onJoinThread={onJoinThread}
              onNewThread={() => setShowCreateThread(true)}
              onGoToView={(v) => {
                setActiveView(v);
                setViewFilter('');
              }}
            />
          ) : activeView === 'todos' ? (
            <div className="today-card today-list todos-view-card">
              {!todos ? (
                <div className="today-empty">Loading…</div>
              ) : todos.length === 0 ? (
                <div className="today-empty">No todos yet.</div>
              ) : (
                todos.map((t) => (
                  <TodoRow
                    key={t.id}
                    todo={t}
                    onToggle={handleToggleTodo}
                    onOpenThread={onJoinThread}
                    onOpenDetail={() =>
                      setDetailItem({
                        id: t.id,
                        title: t.text,
                        body: t.text || '',
                        roomId: t.roomId,
                        roomName: t.roomName,
                        by: t.createdBy,
                        at: t.createdAt,
                        tagLabel: t.completed ? 'done' : 'todo',
                        tagClass: 'kind-todo',
                      })
                    }
                  />
                ))
              )}
            </div>
          ) : activeView === 'decisions' || activeView === 'artifacts' ? (
            <LandingView
              view={activeView}
              items={landingItems}
              loading={loadingLanding}
              filter={viewFilter}
              onFilterChange={setViewFilter}
              sort={viewSort}
              onSortChange={setViewSort}
              onJoinThread={onJoinThread}
              onOpenDetail={setDetailItem}
              canEdit={canEditContent(currentRole)}
            />
          ) : rooms.length === 0 ? (
            <div className="empty-state">
              <p>No threads yet.</p>
              <button
                type="button"
                className="btn-primary empty-state-cta"
                onClick={() => setShowCreateThread(true)}
              >
                + New thread
              </button>
            </div>
          ) : (
            <div className="rooms-grid">
              {sortedThreads.map((room) => {
                const isEmpty = room.participantCount === 0;
                // A thread is "live" when at least one of its participants is
                // currently online. Otherwise it greys out — even if people
                // have joined in the past, no one is actually present right now.
                const hasOnlineParticipant = (room.participants || []).some(
                  (p) => onlineUsers[p.id]?.status === 'online',
                );
                const seen = (lastSeenCounts || {})[room.id] || 0;
                const visible = room.chatMessageCount ?? room.messageCount ?? 0;
                const unread = Math.max(0, visible - seen);
                return (
                  <div
                    key={room.id}
                    className={`room-card ${!hasOnlineParticipant ? 'inactive' : ''} ${
                      room.id === activeThreadId ? 'active' : ''
                    }`}
                    onClick={() => onJoinThread(room.id)}
                  >
                    <div className="room-card-header">
                      <h3>
                        <EntityIcon icon={room.icon} name={room.name} size={18} />
                        {room.name}
                        {unread > 0 && <span className="unread-badge">{unread}</span>}
                      </h3>
                      <div className="room-card-badges">
                        {room.template && room.template !== 'freeform' && (
                          <span className="room-template-badge">
                            {ROOM_TEMPLATES.find((t) => t.id === room.template)?.label ||
                              room.template}
                          </span>
                        )}
                        {isEmpty ? (
                          <span className="room-status empty">Empty</span>
                        ) : (
                          <span className="participant-count">
                            {room.participantCount}{' '}
                            {room.participantCount === 1 ? 'person' : 'people'}
                          </span>
                        )}
                        {onDeleteThread && isAdmin && (
                          <button
                            className="btn-delete-room"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete "${room.name}" and all its messages?`)) {
                                onDeleteThread(room.id);
                              }
                            }}
                            title="Delete thread"
                            aria-label="Delete thread"
                          >
                            <IconClose size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="room-card-meta">
                      <span>Created by {room.createdBy}</span>
                      <span>{room.chatMessageCount ?? room.messageCount} messages</span>
                    </div>
                    {(room.decisionCount > 0 ||
                      room.artifactCount > 0 ||
                      room.todoCount > 0 ||
                      unread > 0) && (
                      <div className="room-stats" aria-label="Thread health">
                        {room.decisionCount > 0 && (
                          <span className="room-stat" title="Active decisions">
                            <IconDecision size={12} />
                            {room.decisionCount}
                          </span>
                        )}
                        {room.artifactCount > 0 && (
                          <span className="room-stat" title="Artifacts">
                            <IconArtifact size={12} />
                            {room.artifactCount}
                          </span>
                        )}
                        {room.todoCount > 0 && (
                          <span className="room-stat" title="Open todos">
                            <IconTodo size={12} />
                            {room.todoCount}
                          </span>
                        )}
                        {unread > 0 && (
                          <span className="room-stat unread" title="Unread messages">
                            <IconMessage size={12} />
                            {unread} unread
                          </span>
                        )}
                      </div>
                    )}
                    <div className="room-card-participants">
                      {room.participants.map((p) => {
                        const presence = onlineUsers[p.id];
                        const status = presence ? presence.status : 'offline';
                        return (
                          <span
                            key={p.id}
                            className="participant-chip"
                            style={{ borderColor: p.color }}
                          >
                            <span className={`chip-presence ${status}`} />
                            {p.avatar &&
                              (isImageIcon(p.avatar) ? (
                                <EntityIcon
                                  icon={p.avatar}
                                  name={p.name}
                                  size={16}
                                  className="chip-avatar"
                                />
                              ) : (
                                <span className="chip-avatar">{p.avatar}</span>
                              ))}
                            {p.name}
                          </span>
                        );
                      })}
                      {room.aiParticipants.map((ai) => {
                        const sparkleColor = ai.color || '#d96f43';
                        const bgColor =
                          sparkleColor === '#127c6d'
                            ? 'rgba(217,111,67,0.22)'
                            : 'rgba(18,124,109,0.22)';
                        return (
                          <span key={ai.id} className="participant-chip ai-chip">
                            <span
                              className="chip-ai-avatar"
                              style={{ color: sparkleColor, background: bgColor }}
                            >
                              <IconSparkle size={10} />
                            </span>
                            {ai.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {currentWorkspace && activeView === 'today' && (
        <CollaboratorsBadge
          members={workspaceMembers}
          onlineUsers={onlineUsers}
          isAdmin={canManage}
          onInvite={() => setShowInvitePanel(true)}
        />
      )}
      {currentWorkspace && <CommandBar hero={!caughtUp} onOpen={() => setAskOpen(true)} />}
      {askOpen && (
        <AskSheet
          onClose={() => setAskOpen(false)}
          onAsk={handleAsk}
          onSeeDecisions={() => {
            setActiveView('decisions');
            setViewFilter('');
          }}
          onSeeTodos={() => {
            setActiveView('todos');
            setViewFilter('');
          }}
          onMarkCaughtUp={handleMarkCaughtUp}
        />
      )}
      <ItemDetailModal
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onSave={handleSaveArtifact}
        onJoinThread={(roomId) => {
          setDetailItem(null);
          onJoinThread(roomId);
        }}
      />
      <Toast message={lobbyToast} />
      <InstallPrompt
        open={showInstallPrompt}
        pwa={pwa}
        onClose={() => setShowInstallPrompt(false)}
      />
    </div>
  );
}

/**
 * InvitePanel — invite teammates (one or a pasted list, with remembered people
 * suggested), view, copy, and revoke invite links, and manage the member list.
 * A bulk invite puts the whole batch on one team link; pending invites are
 * grouped by that link. Open to anyone who can manage the workspace: admins/owners
 * always, plus plain members while the workspace's delegated member permissions
 * are on. The admin-only extras (`isAdmin`) are the delegation toggle itself
 * and blocking/restoring a member's access. Lazily fetches data when opened.
 */
function InvitePanel({ workspace, user, isAdmin = false, onWorkspaceUpdated, onClose }) {
  const [invites, setInvites] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteeName, setInviteeName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [creating, setCreating] = useState(false);
  // The last one-person link's outcome, shown in the same result box as a batch.
  const [linkResult, setLinkResult] = useState(null);
  // Bulk invite: the people picked in InviteTeammates, their role, and the
  // last batch's outcome (its single team link + anyone skipped).
  const [teammates, setTeammates] = useState([]);
  const [teamRole, setTeamRole] = useState('member');
  const [sendingTeam, setSendingTeam] = useState(false);
  const [teamResult, setTeamResult] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [memberPermissions, setMemberPermissions] = useState(
    workspace.memberPermissionsEnabled !== false,
  );
  const [savingPermissions, setSavingPermissions] = useState(false);

  const authHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${user.token}`,
    }),
    [user.token],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [invRes, memRes] = await Promise.all([
        fetch(`${SERVER_URL}/api/workspaces/${workspace.id}/invites`, {
          headers: authHeaders(),
        }),
        fetch(`${SERVER_URL}/api/workspaces/${workspace.id}/members`, {
          headers: authHeaders(),
        }),
      ]);
      if (!invRes.ok) throw new Error('Could not load invites');
      if (!memRes.ok) throw new Error('Could not load members');
      const invData = await invRes.json();
      const memData = await memRes.json();
      setInvites(invData.invites || []);
      setMembers(memData.members || []);
    } catch (e) {
      setError(e.message || 'Failed to load invite data');
    } finally {
      setLoading(false);
    }
  }, [workspace.id, authHeaders]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createInvite = async (e) => {
    e.preventDefault();
    if (!inviteeName.trim()) {
      setError('Please enter the invitee name');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(`${SERVER_URL}/api/workspaces/${workspace.id}/invites`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          inviteeName: inviteeName.trim(),
          email: inviteEmail.trim() || null,
          role: inviteRole,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not create invite');
      setLinkResult(data.invite ? { invite: data.invite, emailed: Boolean(data.emailed) } : null);
      setInviteeName('');
      setInviteEmail('');
      await refresh();
    } catch (e) {
      setError(e.message || 'Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const inviteTeammates = async () => {
    if (teammates.length === 0) return;
    setSendingTeam(true);
    setError(null);
    try {
      const result = await sendBulkInvites(workspace.id, teammates, teamRole);
      setTeamResult(result);
      setTeammates([]);
      track('invites-sent', { count: result.invites.length, source: 'invite-panel' });
      await refresh();
    } catch (e) {
      setError(e.message || 'Failed to send invites');
    } finally {
      setSendingTeam(false);
    }
  };

  const revokeInvite = async (inviteId, name) => {
    const label = name ? `${name}'s invite` : 'this invite';
    if (!window.confirm(`Revoke ${label}? It can no longer be used.`)) return;
    try {
      const res = await fetch(`${SERVER_URL}/api/workspaces/${workspace.id}/invites/${inviteId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Could not revoke invite');
      await refresh();
    } catch (e) {
      setError(e.message || 'Failed to revoke invite');
    }
  };

  const toggleMemberPermissions = async (enabled) => {
    setSavingPermissions(true);
    setError(null);
    try {
      const res = await fetch(`${SERVER_URL}/api/workspaces/${workspace.id}/member-permissions`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Could not update member permissions');
      }
      setMemberPermissions(enabled);
      onWorkspaceUpdated?.(workspace.id, { memberPermissionsEnabled: enabled });
    } catch (e) {
      setError(e.message || 'Failed to update member permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const setMemberBlocked = async (memberId, memberName, blocked) => {
    if (
      blocked &&
      !window.confirm(
        `Block ${memberName}?\n\n` +
          'They lose access to this workspace immediately and any pending invite ' +
          'addressed to them is invalidated. Nothing they wrote is deleted, and ' +
          'you can restore their access at any time.',
      )
    ) {
      return;
    }
    try {
      const res = await fetch(
        `${SERVER_URL}/api/workspaces/${workspace.id}/members/${memberId}/${
          blocked ? 'block' : 'unblock'
        }`,
        { method: 'POST', headers: authHeaders() },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Could not update access');
      }
      await refresh();
    } catch (e) {
      setError(e.message || 'Failed to update access');
    }
  };

  const removeMember = async (memberId, memberName) => {
    if (memberId === user.id) {
      window.alert(
        "You can't remove yourself from this panel. Use the workspace switcher instead.",
      );
      return;
    }
    if (!window.confirm(`Remove ${memberName} from this workspace?`)) return;
    try {
      const res = await fetch(`${SERVER_URL}/api/workspaces/${workspace.id}/members/${memberId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Could not remove member');
      }
      await refresh();
    } catch (e) {
      setError(e.message || 'Failed to remove member');
    }
  };

  const inviteMessage = (inv) => {
    const greeting = inv.inviteeName ? `Hi ${inv.inviteeName},` : 'Hi there,';
    return `${greeting}\n\nPlease join my ${workspace.name} workspace on Symphonate!\n\n${inviteLink(inv.code)}`;
  };
  const copyInviteMessage = (inv) => copyText(inviteMessage(inv));
  const copyLink = (code) => {
    copyText(inviteLink(code));
    setCopiedCode(code);
  };

  // Both invite forms share the role picker, and show the link they made in the
  // same copyable box. Only admins can mint an admin — a member inviting under
  // the delegation would otherwise have a one-hop path to admin rights.
  const roleSelect = (value, onChange, label) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
      <option value="member">Member</option>
      {isAdmin && <option value="admin">Admin</option>}
      <option value="viewer">Viewer</option>
    </select>
  );
  const linkBox = (code) => (
    <div className="team-link-box">
      <code className="team-link-url">{inviteLink(code)}</code>
      <button type="button" className="invite-add-btn" onClick={() => copyLink(code)}>
        {copiedCode === code ? 'Copied' : 'Copy link'}
      </button>
    </div>
  );

  // Pending invites, grouped: each team link (bulk invite) becomes one group
  // with its people under it; single invites stay as their own rows.
  const inviteGroups = [];
  const groupByBatch = new Map();
  for (const inv of invites) {
    if (!inv.batchCode) {
      inviteGroups.push({ key: inv.id, single: inv });
      continue;
    }
    let group = groupByBatch.get(inv.batchCode);
    if (!group) {
      group = { key: inv.batchCode, batchCode: inv.batchCode, people: [] };
      groupByBatch.set(inv.batchCode, group);
      inviteGroups.push(group);
    }
    group.people.push(inv);
  }
  const memberEmails = members.map((m) => m.email).filter(Boolean);
  const pendingEmails = invites.map((i) => i.email).filter(Boolean);
  const inviteRow = (inv, { inTeam = false } = {}) => (
    <li key={inv.id} className={`invite-row${inTeam ? ' invite-row-team' : ''}`}>
      <div className="invite-row-info">
        {inv.inviteeName && <span className="invite-name">{inv.inviteeName}</span>}
        <span className={`role-badge role-${inv.role}`}>{inv.role}</span>
        {inv.email && <span className="invite-email">{inv.email}</span>}
        {!inTeam && <span className="invite-code">{inv.code}</span>}
      </div>
      <div className="invite-row-actions">
        {!inTeam && (
          <button
            onClick={() => copyInviteMessage(inv)}
            title="Copy a ready-to-send invite message with the link"
          >
            Copy invite
          </button>
        )}
        <button onClick={() => revokeInvite(inv.id, inv.inviteeName)} title="Revoke invite">
          Revoke
        </button>
      </div>
    </li>
  );

  return (
    <div className="invite-panel">
      <div className="invite-panel-header">
        <h3>Invite people to {workspace.name}</h3>
        <button className="btn-close" onClick={onClose} aria-label="Close invite panel">
          x
        </button>
      </div>

      {error && <div className="invite-error">{error}</div>}

      {isAdmin && (
        <div className="invite-section member-permissions">
          <label className="member-permissions-toggle">
            <input
              type="checkbox"
              checked={memberPermissions}
              disabled={savingPermissions}
              onChange={(e) => toggleMemberPermissions(e.target.checked)}
            />
            <span>
              <strong>Let members manage this workspace</strong>
              <span className="member-permissions-hint">
                Members can invite people, rename the workspace, change the AI teammates, and remove
                other members and viewers. Turn this off to keep those actions to admins and owners.
                Changing roles, blocking people, and deleting the workspace are always admin-only.
              </span>
            </span>
          </label>
        </div>
      )}

      <div className="invite-section invite-form-section">
        <h4>Invite teammates</h4>
        <p className="invite-section-hint">
          Add one person or paste a list — everyone in the batch shares one link.
        </p>
        <InviteTeammates
          value={teammates}
          onChange={(next) => {
            setTeammates(next);
            setTeamResult(null);
          }}
          memberEmails={memberEmails}
          pendingEmails={pendingEmails}
          disabled={sendingTeam}
        />
        <div className="invite-actions">
          {roleSelect(teamRole, setTeamRole, 'Role for these teammates')}
          <button
            type="button"
            className="btn-primary"
            onClick={inviteTeammates}
            disabled={sendingTeam || teammates.length === 0}
          >
            {sendingTeam
              ? 'Sending…'
              : teammates.length > 1
                ? `Invite ${teammates.length} people`
                : 'Invite'}
          </button>
        </div>
        {teamResult && (
          <div className="invite-result" role="status">
            {teamResult.batchCode ? (
              <>
                <span>
                  {teamResult.emailed ? 'Invites emailed. ' : ''}One link for everyone you just
                  invited:
                </span>
                {linkBox(teamResult.batchCode)}
              </>
            ) : (
              <span>Nobody new to invite.</span>
            )}
            {teamResult.skipped?.length > 0 && (
              <span className="invite-section-hint">
                Skipped {teamResult.skipped.map((x) => x.email).join(', ')} (already in or invited).
              </span>
            )}
          </div>
        )}
      </div>

      <form className="invite-section invite-form-section" onSubmit={createInvite}>
        <h4>Or create a link for one person</h4>
        <p className="invite-section-hint">
          Add their email so only they can use it, or leave it blank to share it anywhere.
        </p>
        <div className="invite-link-fields">
          <input
            type="text"
            placeholder="Their name"
            aria-label="Their name"
            value={inviteeName}
            onChange={(e) => {
              setInviteeName(e.target.value);
              setLinkResult(null);
            }}
            disabled={creating}
            required
          />
          <input
            type="email"
            placeholder="Email (optional)"
            aria-label="Their email (optional)"
            value={inviteEmail}
            onChange={(e) => {
              setInviteEmail(e.target.value);
              setLinkResult(null);
            }}
            disabled={creating}
          />
        </div>
        <div className="invite-actions">
          {roleSelect(inviteRole, setInviteRole, 'Role for this person')}
          <button type="submit" className="btn-primary" disabled={creating || !inviteeName.trim()}>
            {creating ? 'Creating…' : 'Create link'}
          </button>
        </div>
        {linkResult && (
          <div className="invite-result" role="status">
            <span>
              {linkResult.emailed ? `Invite emailed to ${linkResult.invite.email}. ` : ''}Link for{' '}
              {linkResult.invite.inviteeName}:
            </span>
            {linkBox(linkResult.invite.code)}
          </div>
        )}
      </form>

      <div className="invite-section">
        <h4>Pending invites</h4>
        {loading ? (
          <p className="invite-empty">Loading...</p>
        ) : invites.length === 0 ? (
          <p className="invite-empty">No pending invites.</p>
        ) : (
          <ul className="invite-list">
            {inviteGroups.map((g) =>
              g.single ? (
                inviteRow(g.single)
              ) : (
                <li key={g.key} className="invite-team-group">
                  <div className="invite-team-group-head">
                    <span>
                      <strong>Team link</strong> · {g.people.length}{' '}
                      {g.people.length === 1 ? 'person' : 'people'} pending
                    </span>
                    <button
                      type="button"
                      onClick={() => copyLink(g.batchCode)}
                      title="Copy the link everyone in this batch uses"
                    >
                      {copiedCode === g.batchCode ? 'Copied' : 'Copy link'}
                    </button>
                  </div>
                  <ul className="invite-list">
                    {g.people.map((inv) => inviteRow(inv, { inTeam: true }))}
                  </ul>
                </li>
              ),
            )}
          </ul>
        )}
      </div>

      <div className="invite-section">
        <h4>Members ({members.length})</h4>
        {loading ? (
          <p className="invite-empty">Loading...</p>
        ) : (
          <ul className="member-list">
            {members.map((m) => {
              // Mirrors the server guard: a delegated member may only act on
              // people at or below their own rank, and blocking is admin-only.
              const canActOnMember = m.userId !== user.id && (isAdmin || !isWorkspaceAdmin(m.role));
              return (
                <li key={m.userId} className={`member-row${m.blockedAt ? ' member-blocked' : ''}`}>
                  <div className="member-info">
                    {m.avatar &&
                      (isImageIcon(m.avatar) ? (
                        <EntityIcon
                          icon={m.avatar}
                          name={m.name}
                          size={20}
                          className="member-avatar"
                        />
                      ) : (
                        <span className="member-avatar">{m.avatar}</span>
                      ))}
                    <span className="member-name">{m.name}</span>
                    <span className="member-email">{m.email}</span>
                    <span className={`role-badge role-${m.role}`}>{m.role}</span>
                    {m.blockedAt && <span className="role-badge role-blocked">blocked</span>}
                  </div>
                  {canActOnMember && (
                    <div className="member-row-actions">
                      {isAdmin && m.role !== 'owner' && (
                        <button
                          onClick={() => setMemberBlocked(m.userId, m.name, !m.blockedAt)}
                          title={
                            m.blockedAt
                              ? 'Restore this member’s access'
                              : 'Block access and invalidate any pending invite'
                          }
                        >
                          {m.blockedAt ? 'Restore' : 'Block'}
                        </button>
                      )}
                      <button onClick={() => removeMember(m.userId, m.name)} title="Remove member">
                        Remove
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function CatchupModal({ workspaceName, data, loading, onClose, onJumpToThread }) {
  return (
    <div className="catchup-modal-backdrop" onClick={onClose}>
      <div className="catchup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="catchup-modal-header">
          <h2>Catch up — {workspaceName}</h2>
          <button className="btn-close-panel" onClick={onClose}>
            x
          </button>
        </div>
        {loading && <p className="catchup-loading">Loading…</p>}
        {!loading && !data && <p className="catchup-empty">Could not load catch-up.</p>}
        {!loading && data && (
          <div className="catchup-body">
            <p className="catchup-summary">
              {data.rooms.length} {data.rooms.length === 1 ? 'thread' : 'threads'} ·{' '}
              {data.memberCount} {data.memberCount === 1 ? 'teammate' : 'teammates'} ·{' '}
              {data.todos.open} open todo{data.todos.open === 1 ? '' : 's'} ·{' '}
              {data.decisions.activeCount} active decision
              {data.decisions.activeCount === 1 ? '' : 's'}
            </p>

            {data.decisions.recent.length > 0 && (
              <section className="catchup-section">
                <h3>Recent decisions</h3>
                <ul>
                  {data.decisions.recent.map((d) => (
                    <li key={d.id}>
                      <strong>{d.title}</strong>{' '}
                      <span className="catchup-meta">in {d.roomName}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {data.todos.samples.length > 0 && (
              <section className="catchup-section">
                <h3>Open todos</h3>
                <ul>
                  {data.todos.samples.map((t, i) => (
                    <li key={i}>
                      {t.text}{' '}
                      <span className="catchup-meta">
                        in {t.roomName}
                        {t.ageDays !== null && t.ageDays !== undefined && ` · ${t.ageDays}d old`}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {data.artifacts.recent.length > 0 && (
              <section className="catchup-section">
                <h3>Recently updated artifacts</h3>
                <ul>
                  {data.artifacts.recent.map((a) => (
                    <li key={a.id}>
                      <strong>{a.title}</strong>{' '}
                      <span className="catchup-meta">
                        ({a.type}) in {a.roomName}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="catchup-section">
              <h3>Threads</h3>
              <ul className="catchup-thread-list">
                {data.rooms.map((r) => (
                  <li key={r.id}>
                    <button className="catchup-thread-link" onClick={() => onJumpToThread(r.id)}>
                      {r.name}
                    </button>{' '}
                    <span className="catchup-meta">({r.template})</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * LandingView — renders the Decisions / Artefacts / Plans tab. Each row shows
 * the item title, a one-line snippet, and the source thread (a clickable chip
 * that opens the thread). Filtering matches the item title or the thread name;
 * sorting is by most-recent, title, or thread. All in-memory over the
 * workspace landing payload.
 */
function LandingView({
  view,
  items,
  loading,
  filter,
  onFilterChange,
  sort,
  onSortChange,
  onJoinThread,
  onOpenDetail,
  canEdit = false,
}) {
  const noun = view === 'decisions' ? 'decisions' : 'artefacts';
  const q = filter.trim().toLowerCase();

  const rows = (items || [])
    .filter((it) => {
      if (!q) return true;
      return (
        (it.title || '').toLowerCase().includes(q) || (it.roomName || '').toLowerCase().includes(q)
      );
    })
    .slice()
    .sort((a, b) => {
      if (sort === 'title') return (a.title || '').localeCompare(b.title || '');
      if (sort === 'thread') {
        return (
          (a.roomName || '').localeCompare(b.roomName || '') ||
          (a.title || '').localeCompare(b.title || '')
        );
      }
      return Date.parse(b.at || '') - Date.parse(a.at || '');
    });

  // The small leading tag that distinguishes each item kind.
  const tagFor = (it) => {
    if (view === 'decisions')
      return { label: it.status || 'active', cls: `status-${it.status || 'active'}` };
    const t = it.type || 'note';
    return {
      label: t === 'shortlist' ? 'short list' : t,
      cls: t === 'shortlist' ? 'kind-shortlist' : 'type',
    };
  };

  // Normalise a landing row into the shape ItemDetailModal reads. `editable`
  // marks the rows the lightbox may edit in place: real artefacts only — short
  // lists live in their own table and are curated item-by-item in the thread,
  // and decisions aren't free-text edits.
  const detailFor = (it, tag) => ({
    id: it.id,
    title: it.title,
    body: it.body || '',
    roomId: it.roomId,
    roomName: it.roomName,
    by: it.by,
    at: it.at,
    tagLabel: tag.label,
    tagClass: tag.cls,
    editable: canEdit && view === 'artifacts' && it.type !== 'shortlist',
  });

  return (
    <div className="landing-view">
      <div className="landing-toolbar">
        <input
          type="search"
          className="landing-filter"
          placeholder={`Filter ${noun} by title or thread…`}
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          aria-label={`Filter ${noun}`}
        />
        <label className="landing-sort">
          <span>Sort</span>
          <select value={sort} onChange={(e) => onSortChange(e.target.value)}>
            <option value="recent">Most recent</option>
            <option value="title">Title (A–Z)</option>
            <option value="thread">Thread (A–Z)</option>
          </select>
        </label>
      </div>
      {loading && !items ? (
        <p className="landing-loading">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p>{q ? `No ${noun} match “${filter}”.` : `No ${noun} yet.`}</p>
        </div>
      ) : (
        <ul className="landing-list">
          {rows.map((it) => {
            const tag = tagFor(it);
            return (
              <li
                key={`${it.kind || view}-${it.id}`}
                className={`landing-item ${it.completed ? 'completed' : ''}`}
                onClick={() => onOpenDetail && onOpenDetail(detailFor(it, tag))}
                role="button"
                tabIndex={0}
                title="Open to read"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenDetail && onOpenDetail(detailFor(it, tag));
                  }
                }}
              >
                <div className="landing-item-main">
                  <div className="landing-item-title-row">
                    <span className={`landing-item-tag ${tag.cls}`}>{tag.label}</span>
                    <span className="landing-item-title">{it.title}</span>
                  </div>
                  {it.snippet && <p className="landing-item-snippet">{it.snippet}</p>}
                </div>
                <button
                  type="button"
                  className="landing-item-thread"
                  onClick={(e) => {
                    e.stopPropagation();
                    onJoinThread(it.roomId);
                  }}
                  title={`Open “${it.roomName}”`}
                >
                  <IconMessage size={12} />
                  <span>{it.roomName}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default Lobby;
